import { useState } from 'react';
import adminApi from '../../lib/adminApi';

export const MAX_PROFILE_GALLERY_PHOTOS = 20;

export default function ProfilePhotoGalleryEditor({ value = [], onChange, onBusyChange = () => {} }) {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const photos = [...new Set(value.map(item => String(item || '').trim()).filter(Boolean))];
  const addUrl = () => {
    try {
      const parsed = new URL(url.trim());
      if (!['https:', 'http:'].includes(parsed.protocol)) throw new Error();
      if (photos.length >= MAX_PROFILE_GALLERY_PHOTOS) throw new Error();
      onChange([...new Set([...photos, url.trim()])]);
      setUrl(''); setError('');
    } catch { setError('Enter a valid http or https image URL. Maximum 20 photos.'); }
  };
  const upload = async event => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (files.length + photos.length > MAX_PROFILE_GALLERY_PHOTOS) {
      setError('A profile can have up to 20 gallery photos.'); return;
    }
    setUploading(true); onBusyChange(true); setError('');
    const next = [...photos];
    try {
      for (const file of files) {
        const body = new FormData(); body.append('file', file);
        const response = await adminApi.post('/admin/uploads/portfolio-image', body, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (response.data.image_url) next.push(response.data.image_url);
        onChange([...new Set(next)]);
      }
    } catch (e) { setError(e.response?.data?.detail || 'Upload failed. Photos already uploaded are kept; retry the remaining files.'); }
    finally { setUploading(false); onBusyChange(false); }
  };
  const move = (index, offset) => {
    const next = [...photos];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange(next);
  };
  return <fieldset disabled={uploading} className="min-w-0 rounded-3xl border border-orange-200 bg-orange-50/60 p-4">
    <legend className="px-1 font-semibold">Public photo gallery — admin only</legend>
    <p className="text-sm text-gray-600">These photos appear on the public profile. Only admins can add, reorder or remove them. Save the profile to publish your changes.</p>
    <p className="my-2 text-sm font-semibold">{photos.length} / {MAX_PROFILE_GALLERY_PHOTOS} photos</p>
    <label className="block text-sm font-semibold">Upload additional photos
      <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={upload} disabled={uploading || photos.length >= MAX_PROFILE_GALLERY_PHOTOS} className="mt-2 block w-full text-sm" />
    </label>
    <div className="my-3 flex flex-wrap gap-2">
      <input type="url" value={url} onChange={event => setUrl(event.target.value)} aria-label="Additional photo URL" placeholder="Or paste an image URL" className="min-w-0 flex-1 rounded-lg border p-2" />
      <button type="button" onClick={addUrl} disabled={!url.trim() || photos.length >= MAX_PROFILE_GALLERY_PHOTOS} className="rounded-lg border bg-white px-3 py-2 disabled:opacity-50">Add photo</button>
    </div>
    {uploading && <p role="status">Uploading photos… Please wait before saving.</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {photos.map((photo, index) => <li key={photo} className="min-w-0 rounded-xl border bg-white p-2">
        <img src={photo} alt={`Gallery photo ${index + 1}`} loading="lazy" className="h-28 w-full rounded-lg object-contain" />
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <button type="button" disabled={index === 0} aria-label={`Move photo ${index + 1} earlier`} onClick={() => move(index, -1)} className="rounded border px-2 py-1 disabled:opacity-30">←</button>
          <button type="button" disabled={index === photos.length - 1} aria-label={`Move photo ${index + 1} later`} onClick={() => move(index, 1)} className="rounded border px-2 py-1 disabled:opacity-30">→</button>
          <button type="button" aria-label={`Remove photo ${index + 1}`} onClick={() => onChange(photos.filter((_, i) => i !== index))} className="rounded border px-2 py-1 text-red-700">Remove</button>
        </div>
      </li>)}
    </ol>
  </fieldset>;
}
